import React, { FC, useMemo, useState } from 'react';
import { createEditor, Editor } from 'slate';
import { RenderPlaceholderProps, withReact } from 'slate-react';
import { ClickAwayListener, FormControl, FormHelperText, makeStyles } from '@material-ui/core';
import { useField } from 'formik';
import classNames from 'classnames';

import { MentionsInstance } from 'components';
import { Network } from 'api';

interface Props {
  fieldName: string;
  mentionsFieldName: string;
  blockListFieldName: string;
  placeholder: string;
  renderPlaceholder?: (props: RenderPlaceholderProps) => JSX.Element;
  disabled?: boolean;
  editor?: Editor;
  publicQuery: boolean;
  showTextCounter?: boolean;
  disableMentions?: boolean;
  network?: Network;
}

const useStyles = makeStyles(theme => ({
  editor: {
    border: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: 4,

    '&:focus-within': {
      outline: 'none',
      borderColor: theme.palette.primary.main,
    },
  },
  error: {
    border: 0,
  },
}));

const MentionsEditor: FC<Props> = props => {
  const {
    fieldName,
    placeholder,
    renderPlaceholder,
    disabled,
    mentionsFieldName,
    blockListFieldName,
    publicQuery,
    showTextCounter,
    disableMentions,
    network,
  } = props;
  const classes = useStyles();
  const [, meta] = useField(fieldName);
  const [focused, setFocused] = useState(false);
  const hasError = !!meta.error && meta.touched;
  const integratedEditor = useMemo(() => withMentions(withReact(createEditor())), []);

  function handleClickAway() {
    setFocused(false);
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <FormControl
        data-test-id={fieldName}
        error={hasError}
        fullWidth
        focused={focused}
        className={classNames(classes.editor, { [classes.error]: hasError })}
      >
        <MentionsInstance
          editor={integratedEditor}
          fieldName={fieldName}
          mentionsFieldName={mentionsFieldName}
          blockListFieldName={blockListFieldName}
          placeholder={placeholder}
          renderPlaceholder={renderPlaceholder}
          onFocus={() => setFocused(true)}
          disabled={disabled}
          publicQuery={publicQuery}
          showTextCounter={showTextCounter}
          disableMentions={disableMentions}
          network={network}
        />
        {hasError && <FormHelperText>{meta.error}</FormHelperText>}
      </FormControl>
    </ClickAwayListener>
  );
};

export default MentionsEditor;

export function withMentions(editor: Editor) {
  const { isInline, isVoid } = editor;

  editor.isInline = element => {
    //@ts-ignore
    return element.type === 'mention' ? true : isInline(element);
  };

  editor.isVoid = element => {
    //@ts-ignore
    return element.type === 'mention' ? true : isVoid(element);
  };

  return editor;
}
